import React, { useState, useEffect, useMemo } from 'react';
import { ActiveAlertReminders } from '@/components/ActiveAlertReminders';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { endOfDay, endOfMonth, endOfWeek, format, startOfDay, startOfMonth, startOfWeek } from 'date-fns';
import { formatCheckTime } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { useClients } from '@/hooks/useClients';
import { useAlerts } from '@/hooks/useAlerts';
import { useRosters } from '@/hooks/useRosters';
import { usePassExpirationCheck } from '@/hooks/usePassExpirationCheck';
import { Client, Alert, SystemMetrics } from '../types/shelter';
import ClientProfile from './ClientProfile';
import RosterManagement from './RosterManagement';
import { InspectionsModule } from '@/components/inspections/InspectionsModule';
import { ClientsTab } from './clients/ClientsTab';
import AlertDetailDialog from './AlertDetailDialog';
interface StaffActivity {
  id: string;
  staffName: string;
  action: 'check-in' | 'check-out' | 'service';
  clientName?: string;
  serviceType?: string;
  timestamp: string;
  loginTime?: string;
  logoutTime?: string;
}
import johnPhoto from '@/assets/client-john.jpg';
import michaelPhoto from '@/assets/client-michael.jpg';
import robertPhoto from '@/assets/client-robert.jpg';
import davidPhoto from '@/assets/client-david.jpg';
import carlosPhoto from '@/assets/client-carlos.jpg';
import anthonyPhoto from '@/assets/client-anthony.jpg';
import marcusPhoto from '@/assets/client-marcus.jpg';
import stevenPhoto from '@/assets/client-steven.jpg';
import cimfoniLogo from '@/assets/cimfoni_icon_only_4.png';
interface Note {
  id: string;
  content: string;
  author: string;
  timestamp: string;
  isImportant: boolean;
  taggedClients: string[];
  taggedStaff?: string[];
  status: 'active' | 'pending_approval';
  needsApproval?: boolean;
  alertSent?: boolean;
}
import { Users, Activity, AlertTriangle, Building2, LogOut, Cpu, HardDrive, Wifi, Database, BarChart3, TrendingUp, Clock, Shield, ShieldAlert, ShieldCheck, Plus, BedDouble, Bell, Eye, User, Calendar, FileText, BookOpen, CheckCircle2, Phone, Send, Tag, Star, MessageSquare, ChevronUp, ChevronDown, Search, Upload, FolderOpen, HelpCircle, Settings, Home, Circle, ToggleLeft, ClipboardCheck, Loader2, CreditCard, History, ListChecks, StickyNote } from 'lucide-react';
import { ComplianceModule } from './compliance/ComplianceModule';
import ShiftReport from './staff/ShiftReport';
import { supabase } from '@/integrations/supabase/client';
import { useClientPhotos } from '@/hooks/useClientPhotos';
import { useRooms } from '@/hooks/useRooms';
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger, SidebarFooter } from '@/components/ui/sidebar';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PieChart, Pie, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import UserGuide from './UserGuide';
import { AIHelpChat } from './AIHelpChat';
import AlertNotificationSettings from './admin/AlertNotificationSettings';
import { StaffManagement } from './admin/StaffManagement';
import { StaffManagementSection } from './admin/StaffManagementSection';
import SiteManagement from './admin/SiteManagement';
import FacilitySelector from './admin/FacilitySelector';
import SiteDailyCensus from './SiteDailyCensus';
import MasterCensus from './MasterCensus';
import NewClientForm from './NewClientForm';
import OnboardingWizard from './admin/OnboardingWizard';
import EmptyState from './admin/EmptyState';
import { UnifiedUserManagement } from './admin/UnifiedUserManagement';
import { UserActivityLogs } from './admin/UserActivityLogs';
import { FeatureToggleManagement } from './admin/FeatureToggleManagement';
import { useSites } from '@/hooks/useSites';
import { useStaffManagement } from '@/hooks/useStaffManagement';
import { useFeatureToggles } from '@/hooks/useFeatureToggles';

import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { normalizeStatus, getAvailableTransitions, displayStatus, isClientOnPass, getStatusColor as getUniversalStatusColor, resolveStatusKey } from '@/utils/statusTransitions';
import { EnhancedAlertsSection } from '@/components/manager/EnhancedAlertsSection';
import { Input } from '@/components/ui/input';
import { BrandingSettings } from './admin/BrandingSettings';
import { SystemSettings } from './admin/SystemSettings';
import AnalyticsHub from './analytics/AnalyticsHub';
import { ModuleGate } from './billing/ModuleGate';
import { useEntitlements } from '@/hooks/useEntitlements';
import { MyProfileDialog } from './profile/MyProfileDialog';
import { GlobalSearchCommand } from './GlobalSearchCommand';
import BillingPage from './billing/BillingPage';
import PerformanceCard from './admin/PerformanceCard';
import SuperAdminHome from './admin/SuperAdminHome';

// Normalize service type helper
const normalizeServiceType = (type: string): string => {
  const lower = type.toLowerCase();
  if (lower === 'breakfast') return 'breakfast';
  if (lower === 'lunch') return 'lunch';
  if (lower === 'dinner') return 'dinner';
  if (lower === 'milk') return 'milk';
  if (lower === 'juice') return 'juice';
  if (lower.includes('metro') || lower.includes('transit')) return 'metro';
  if (lower.includes('laundry') || lower.includes('wash')) return 'laundry';
  if (lower.includes('meal') || lower.includes('food')) return 'meal';
  if (lower.includes('beverage') || lower.includes('drink')) return 'juice';
  return lower;
};

// Services Analytics Component
const ServicesAnalytics = ({ clients, currentUserName }: {clients: Client[];currentUserName: string;}) => {
  type ServiceKey = 'metro' | 'laundry' | 'breakfast' | 'lunch' | 'dinner' | 'milk' | 'juice';
  type ServiceTotals = Record<ServiceKey, number>;

  const { sites = [] } = useSites();
  const [selectedTimeframe, setSelectedTimeframe] = useState<'daily' | 'weekly' | 'monthly'>('monthly');
  const [selectedService, setSelectedService] = useState<string>('all');
  const [selectedFacility, setSelectedFacility] = useState<string>('all');

  const { data: serviceLogs = [] } = useQuery({
    queryKey: ['all-services-summary'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('service_logs')
        .select('client_id, service_type, quantity, created_at')
        .is('deleted_at', null);

      if (error) throw error;
      return data || [];
    }
  });

  const serviceTypes: Array<{ key: 'all' | ServiceKey; label: string }> = [
    { key: 'all', label: 'All Services' },
    { key: 'metro', label: 'Transit' },
    { key: 'laundry', label: 'Laundry' },
    { key: 'breakfast', label: 'Breakfast' },
    { key: 'lunch', label: 'Lunch' },
    { key: 'dinner', label: 'Dinner' },
    { key: 'milk', label: 'Milk' },
    { key: 'juice', label: 'Juice' }
  ];

  const summaryTypes = serviceTypes.filter((type): type is { key: ServiceKey; label: string } => type.key !== 'all');

  const chartColors: Record<ServiceKey, string> = {
    metro: 'hsl(221 100% 32%)',
    laundry: 'hsl(213 94% 54%)',
    breakfast: 'hsl(152 45% 42%)',
    lunch: 'hsl(40 55% 52%)',
    dinner: 'hsl(16 100% 50%)',
    milk: 'hsl(200 98% 39%)',
    juice: 'hsl(0 84% 60%)'
  };

  const { rangeStart, rangeEnd } = useMemo(() => {
    const now = new Date();

    switch (selectedTimeframe) {
      case 'daily':
        return { rangeStart: startOfDay(now), rangeEnd: endOfDay(now) };
      case 'weekly':
        return { rangeStart: startOfWeek(now), rangeEnd: endOfWeek(now) };
      default:
        return { rangeStart: startOfMonth(now), rangeEnd: endOfMonth(now) };
    }
  }, [selectedTimeframe]);

  const activeFacilityName = selectedFacility === 'all'
    ? 'All Facilities'
    : sites.find((site) => site.id === selectedFacility)?.name || 'Facility';

  const facilityFilteredClients = useMemo(() => {
    if (selectedFacility === 'all') return clients;

    return clients.filter((client) => (
      client.site_id === selectedFacility || (client as any).siteId === selectedFacility
    ));
  }, [clients, selectedFacility]);

  const timeframeLogs = useMemo(() => {
    return serviceLogs.filter((log: any) => {
      if (!log.created_at) return false;
      const createdAt = new Date(log.created_at);
      return createdAt >= rangeStart && createdAt <= rangeEnd;
    });
  }, [serviceLogs, rangeEnd, rangeStart]);

  const filteredData = useMemo(() => {
    return facilityFilteredClients
      .map((client) => {
        const totals: ServiceTotals = {
          metro: 0,
          laundry: 0,
          breakfast: 0,
          lunch: 0,
          dinner: 0,
          milk: 0,
          juice: 0
        };

        timeframeLogs.forEach((log: any) => {
          if (log.client_id !== client.id) return;

          const normalized = normalizeServiceType(log.service_type);
          if (normalized in totals) {
            totals[normalized as ServiceKey] += (log.quantity || 1);
          }
        });

        return {
          name: client.name,
          ...totals,
          total: Object.values(totals).reduce((sum, value) => sum + value, 0)
        };
      })
      .filter((row) => row.total > 0);
  }, [facilityFilteredClients, timeframeLogs]);

  const totalsByType = useMemo<ServiceTotals>(() => ({
    metro: filteredData.reduce((sum, row) => sum + row.metro, 0),
    laundry: filteredData.reduce((sum, row) => sum + row.laundry, 0),
    breakfast: filteredData.reduce((sum, row) => sum + row.breakfast, 0),
    lunch: filteredData.reduce((sum, row) => sum + row.lunch, 0),
    dinner: filteredData.reduce((sum, row) => sum + row.dinner, 0),
    milk: filteredData.reduce((sum, row) => sum + row.milk, 0),
    juice: filteredData.reduce((sum, row) => sum + row.juice, 0)
  }), [filteredData]);

  const baseChartData = useMemo(() => {
    return summaryTypes.map((type) => ({
      key: type.key,
      name: type.label,
      value: totalsByType[type.key],
      color: chartColors[type.key]
    }));
  }, [summaryTypes, totalsByType]);

  const chartData = useMemo(() => {
    if (selectedService === 'all') return baseChartData;
    return baseChartData.filter((item) => item.key === selectedService);
  }, [baseChartData, selectedService]);

  const pieData = chartData.filter((item) => item.value > 0);
  const hasChartData = chartData.some((item) => item.value > 0);
  const timeLabel = selectedTimeframe.charAt(0).toUpperCase() + selectedTimeframe.slice(1);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const rows = filteredData.map((row) => `
      <tr>
        <td>${row.name}</td>
        ${selectedService === 'all' || selectedService === 'metro' ? `<td style="text-align:center">${row.metro}</td>` : ''}
        ${selectedService === 'all' || selectedService === 'laundry' ? `<td style="text-align:center">${row.laundry}</td>` : ''}
        ${selectedService === 'all' || selectedService === 'breakfast' ? `<td style="text-align:center">${row.breakfast}</td>` : ''}
        ${selectedService === 'all' || selectedService === 'lunch' ? `<td style="text-align:center">${row.lunch}</td>` : ''}
        ${selectedService === 'all' || selectedService === 'dinner' ? `<td style="text-align:center">${row.dinner}</td>` : ''}
        ${selectedService === 'all' || selectedService === 'milk' ? `<td style="text-align:center">${row.milk}</td>` : ''}
        ${selectedService === 'all' || selectedService === 'juice' ? `<td style="text-align:center">${row.juice}</td>` : ''}
        ${selectedService === 'all' ? `<td style="text-align:center;font-weight:bold">${row.total}</td>` : ''}
      </tr>
    `).join('');

    printWindow.document.write(`<!DOCTYPE html><html><head><title>Services Report</title>
      <style>
        body{font-family:Arial,sans-serif;margin:30px;color:#000}
        h1{font-size:20pt;margin:0 0 4px}
        h2{font-size:14pt;font-weight:normal;margin:0 0 4px;color:#444}
        .header{text-align:center;border-bottom:2px solid #000;padding-bottom:12px;margin-bottom:20px}
        .date{font-size:10pt;color:#666}
        table{width:100%;border-collapse:collapse;margin-top:16px}
        th,td{padding:8px 12px;border:1px solid #ddd;text-align:left}
        th{background:#f5f5f5;font-weight:600}
        .summary{display:flex;flex-wrap:wrap;gap:12px;margin-top:20px}
        .summary-box{flex:1;min-width:100px;border:1px solid #ccc;padding:12px;text-align:center}
        .summary-box .num{font-size:18pt;font-weight:bold}
        .summary-box .label{font-size:9pt;color:#666}
        .footer{margin-top:30px;text-align:center;font-size:8pt;color:#666;border-top:1px solid #ccc;padding-top:8px}
        @media print{button{display:none}}
      </style></head><body>
      <div class="header">
        <h1>Services Report — ${timeLabel}</h1>
        <h2>${activeFacilityName}</h2>
        <div class="date">Generated: ${format(new Date(), 'MMMM d, yyyy h:mm a')}</div>
      </div>
      <table>
        <thead><tr>
          <th>Client Name</th>
          ${selectedService === 'all' || selectedService === 'metro' ? '<th style="text-align:center">Transit</th>' : ''}
          ${selectedService === 'all' || selectedService === 'laundry' ? '<th style="text-align:center">Laundry</th>' : ''}
          ${selectedService === 'all' || selectedService === 'breakfast' ? '<th style="text-align:center">Breakfast</th>' : ''}
          ${selectedService === 'all' || selectedService === 'lunch' ? '<th style="text-align:center">Lunch</th>' : ''}
          ${selectedService === 'all' || selectedService === 'dinner' ? '<th style="text-align:center">Dinner</th>' : ''}
          ${selectedService === 'all' || selectedService === 'milk' ? '<th style="text-align:center">Milk</th>' : ''}
          ${selectedService === 'all' || selectedService === 'juice' ? '<th style="text-align:center">Juice</th>' : ''}
          ${selectedService === 'all' ? '<th style="text-align:center">Total</th>' : ''}
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="summary">
        <div class="summary-box"><div class="num">${totalsByType.metro}</div><div class="label">Transit</div></div>
        <div class="summary-box"><div class="num">${totalsByType.laundry}</div><div class="label">Laundry</div></div>
        <div class="summary-box"><div class="num">${totalsByType.breakfast}</div><div class="label">Breakfast</div></div>
        <div class="summary-box"><div class="num">${totalsByType.lunch}</div><div class="label">Lunch</div></div>
        <div class="summary-box"><div class="num">${totalsByType.dinner}</div><div class="label">Dinner</div></div>
        <div class="summary-box"><div class="num">${totalsByType.milk}</div><div class="label">Milk</div></div>
        <div class="summary-box"><div class="num">${totalsByType.juice}</div><div class="label">Juice</div></div>
      </div>
      <div class="footer">Generated by ${currentUserName} · Confidential — For Internal Use Only</div>
    </body></html>`);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-lg border bg-card/50 p-4 sm:flex-row">
        <div className="flex items-center gap-3">
          <label className="whitespace-nowrap text-sm font-medium">Time Period:</label>
          <Select value={selectedTimeframe} onValueChange={(value) => setSelectedTimeframe(value as 'daily' | 'weekly' | 'monthly')}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-3">
          <label className="whitespace-nowrap text-sm font-medium">Service Type:</label>
          <Select value={selectedService} onValueChange={setSelectedService}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {serviceTypes.map((type) => (
                <SelectItem key={type.key} value={type.key}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-3">
          <label className="whitespace-nowrap text-sm font-medium">Facility:</label>
          <Select value={selectedFacility} onValueChange={setSelectedFacility}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Facilities</SelectItem>
              {sites.map((site) => (
                <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button onClick={handlePrint} className="ml-auto">
          <FileText className="mr-2 h-4 w-4" />
          Print Report
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        {summaryTypes.map((type) => (
          <Card key={type.key} className="p-3">
            <div className="text-center">
              <div className="text-xl font-bold text-primary">{totalsByType[type.key]}</div>
              <div className="text-xs text-muted-foreground">{type.label}</div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Service Distribution — {timeLabel}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {selectedService === 'all'
                ? `Breakdown of all services for ${activeFacilityName}`
                : `${serviceTypes.find((type) => type.key === selectedService)?.label ?? 'Selected Service'} totals for ${activeFacilityName}`}
            </p>
          </CardHeader>
          <CardContent>
            {hasChartData ? (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    dataKey="value"
                    nameKey="name"
                    labelLine={false}
                    outerRadius={110}
                    label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry) => (
                      <Cell key={entry.key} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [value, 'Services']}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.5rem'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[320px] items-center justify-center text-sm text-muted-foreground">
                No service data for this period
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Service Totals — {timeLabel}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Visual totals by service type for the selected filters
            </p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                <Tooltip
                  formatter={(value) => [value, 'Services']}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '0.5rem'
                  }}
                />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {chartData.map((entry) => (
                    <Cell key={entry.key} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const {
    user,
    logout,
    userRole
  } = useAuth();
  const { clients, isLoading: clientsLoading, updateClient, createClient, changeStatus, isChangingStatus } = useClients();
  const queryClient = useQueryClient();
  const { alerts = [], isLoading: alertsLoading } = useAlerts();
  usePassExpirationCheck();
  const { rooms } = useRooms();
  const { sites = [], isLoading: sitesLoading } = useSites();
  const { staff: staffMembers = [] } = useStaffManagement();
  const { isFeatureEnabled, isLoading: featureTogglesLoading } = useFeatureToggles();
  const { hasModule } = useEntitlements();
  const isSuperAdminForGate = userRole === 'super_admin';
  // Gate sidebar entry: visible if subscribed OR if super_admin (to access upsell).
  const showModuleEntry = (key: 'inspections' | 'ai_assistant' | 'training' | 'advanced_analytics') =>
    hasModule(key) || isSuperAdminForGate;
  const { rosters } = useRosters();

  // Build set of rostered client IDs from today's roster only
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const todaysRosters = useMemo(() => {
    return rosters.filter((r: any) => r.roster_date === todayStr);
  }, [rosters, todayStr]);

  const rosteredClientIds = useMemo(() => {
    const ids = new Set<string>();
    todaysRosters.forEach((roster: any) => {
      (roster.roster_entries || []).forEach((entry: any) => {
        const clientId = entry.client_id || entry.client?.id;
        if (clientId) ids.add(clientId);
      });
    });
    return ids;
  }, [todaysRosters]);

  // Clients for Daily Census: use today's roster if one exists, otherwise show all clients
  const rosteredClients = useMemo(() => {
    if (todaysRosters.length === 0) return clients;
    return clients.filter((c) => rosteredClientIds.has(c.id));
  }, [clients, rosteredClientIds, todaysRosters]);

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showMyProfile, setShowMyProfile] = useState(false);

  // Check if user needs onboarding (first-time users with no facilities)
  // Wait until sites have finished loading to avoid flashing the wizard
  useEffect(() => {
    if (sitesLoading) return;
    if (sites.length === 0 && (userRole === 'manager' || userRole === 'super_admin' || userRole === 'program_director')) {
      setShowOnboarding(true);
    } else if (sites.length > 0) {
      setShowOnboarding(false);
    }
  }, [sites.length, userRole, sitesLoading]);
  const [alertSortBy, setAlertSortBy] = useState<'recent' | 'category' | 'type'>('recent');
  const [alertViewTab, setAlertViewTab] = useState<'active' | 'resolved'>('active');

  // Roster state
  const [rosterSearchTerm, setRosterSearchTerm] = useState('');
  const [rosterSortField, setRosterSortField] = useState<'name' | 'room' | 'status' | 'checkInTime' | 'caseWorker'>('checkInTime');
  const [rosterSortDirection, setRosterSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isCheckinDialogOpen, setIsCheckinDialogOpen] = useState(false);
  const [checkinClient, setCheckinClient] = useState<Client | null>(null);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
  const [isCheckoutDialogOpen, setIsCheckoutDialogOpen] = useState(false);
  const [checkoutClient, setCheckoutClient] = useState<Client | null>(null);

  // Roster helpers — uses universal color system from statusTransitions
  const getStatusColor = (status: Client['status'], client?: Client) => {
    const key = client ? resolveStatusKey(client as any) : (normalizeStatus(status) as any);
    return getUniversalStatusColor(key, 'solid');
  };
  const getStatusIcon = (status: Client['status']) => {
    const s = normalizeStatus(status);
    switch (s) {
      case 'checked-in':return <CheckCircle2 className="h-3 w-3" />;
      case 'checked-out':return <Circle className="h-3 w-3" />;
      case 'not_present':return <AlertTriangle className="h-3 w-3" />;
      case 'unbedded':return <AlertTriangle className="h-3 w-3" />;
      default:return null;
    }
  };
  const handleRosterSort = (field: 'name' | 'room' | 'status' | 'checkInTime' | 'caseWorker') => {
    if (rosterSortField === field) {
      setRosterSortDirection(rosterSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setRosterSortField(field);
      setRosterSortDirection('asc');
    }
  };
  const getRosterSortedClients = () => {
    let filtered = rosteredClients;
    if (rosterSearchTerm) {
      const term = rosterSearchTerm.toLowerCase();
      filtered = rosteredClients.filter((c) =>
      (c.name || '').toLowerCase().includes(term) ||
      (c.room || '').toLowerCase().includes(term) ||
      (c.caseWorker || '').toLowerCase().includes(term)
      );
    }
    return filtered.sort((a, b) => {
      let aVal: string | number | Date;
      let bVal: string | number | Date;
      switch (rosterSortField) {
        case 'name':aVal = a.name || '';bVal = b.name || '';break;
        case 'room':aVal = a.room || 'zzz';bVal = b.room || 'zzz';break;
        case 'status':aVal = a.status || '';bVal = b.status || '';break;
        case 'checkInTime':
          aVal = a.checkInTime ? new Date(a.checkInTime) : new Date(0);
          bVal = b.checkInTime ? new Date(b.checkInTime) : new Date(0);
          break;
        case 'caseWorker':aVal = a.caseWorker || '';bVal = b.caseWorker || '';break;
        default:aVal = a.name || '';bVal = b.name || '';
      }
      if (rosterSortDirection === 'asc') return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });
  };
  const handleCheckIn = (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    if (client) {setCheckinClient(client);setIsCheckinDialogOpen(true);}
  };
  const confirmCheckIn = () => {
    if (checkinClient) {
      if (!checkinClient.room_id) {
        toast({ title: "Room Required", description: "Assign a room to this client before checking in.", variant: "destructive" });
        setIsCheckinDialogOpen(false); setCheckinClient(null);
        return;
      }
      changeStatus({ id: checkinClient.id, newStatus: 'checked-in' });
    }
    setIsCheckinDialogOpen(false);setCheckinClient(null);
  };
  const handleCheckOut = (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    if (client) {setCheckoutClient(client);setIsCheckoutDialogOpen(true);}
  };
  const confirmCheckOut = () => {
    if (checkoutClient) changeStatus({ id: checkoutClient.id, newStatus: 'checked-out' });
    setIsCheckoutDialogOpen(false);setCheckoutClient(null);
  };

  // Get category color
  const getCategoryColor = (category?: string) => {
    switch (category) {
      case 'staff':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'service':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'building':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'system':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-red-100 text-red-800 border-red-200';
    }
  };

  // Map client alert types to category
  const getClientAlertCategory = (alertText: string): 'service' | 'client' => {
    const serviceAlerts = ['Received Transit', 'Received Laundry card', '2 Lunch', '3 Juice'];
    if (serviceAlerts.some((alert) => alertText.includes(alert.replace(/\d+\s/, '')))) return 'service';
    return 'client';
  };

  // Memoized combined alerts — avoids recomputing on every render
  const allAlerts = useMemo(() => {
    const systemAlerts = alerts;
    const clientAlerts: Alert[] = [];

    clients.forEach((client) => {
      if (client.alerts && client.alerts.length > 0) {
        client.alerts.forEach((alertText, index) => {
          clientAlerts.push({
            id: `client-${client.id}-${index}`,
            type: 'info',
            title: alertText,
            description: `Client alert for ${client.name} - Room ${client.room}`,
            timestamp: new Date().toISOString(),
            status: 'active',
            createdBy: 'Staff Member',
            clientId: client.id,
            category: getClientAlertCategory(alertText)
          });
        });
      }
    });

    const seen = new Set<string>();
    const deduped: Alert[] = [];
    for (const alert of systemAlerts) {
      if (alert.status !== 'active') continue;
      const key = `${alert.title}::${alert.clientId || ''}`;
      seen.add(key);
      deduped.push(alert);
    }
    for (const alert of clientAlerts) {
      const key = `${alert.title}::${alert.clientId || ''}`;
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(alert);
      }
    }

    return deduped.sort((a, b) => {
      switch (alertSortBy) {
        case 'recent':
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        case 'category':{
            const categoryOrder: Record<string, number> = { building: 0, staff: 1, client: 2, service: 3, system: 4 };
            return (categoryOrder[a.category || 'system'] ?? 5) - (categoryOrder[b.category || 'system'] ?? 5);
          }
        case 'type':{
            const typeOrder: Record<string, number> = { critical: 0, warning: 1, info: 2 };
            return typeOrder[a.type] - typeOrder[b.type];
          }
        default:
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      }
    });
  }, [alerts, clients, alertSortBy]);
  const [activeSection, setActiveSection] = useState<'home' | 'clients' | 'settings' | 'staff' | 'notes' | 'alerts' | 'analytics' | 'search' | 'help' | 'services' | 'ai-chat' | 'ai-assistant' | 'notifications' | 'facilities' | 'user-management' | 'site-management' | 'activity-logs' | 'shift-report' | 'feature-toggles' | 'training' | 'inspections' | 'rosters' | 'billing' | 'compliance' | null>(userRole === 'super_admin' ? 'home' : 'clients');
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [selectedSiteName, setSelectedSiteName] = useState<string | null>(null);
  const [isStaffManagementOpen, setIsStaffManagementOpen] = useState(false);
  const [isStaffActivityOpen, setIsStaffActivityOpen] = useState(false);
  const [activeStaffTab, setActiveStaffTab] = useState('overview');
  const [showStaffProfile, setShowStaffProfile] = useState(false);
  const [selectedStaffMember, setSelectedStaffMember] = useState(null);
  const [showAddStaffForm, setShowAddStaffForm] = useState(false);
  const [showServiceAlertsModal, setShowServiceAlertsModal] = useState(false);
  const [isNewClientOpen, setIsNewClientOpen] = useState(false);

  // Mock staff activity data
  const [staffActivity] = useState<StaffActivity[]>([{
    id: '1',
    staffName: 'Sarah Johnson',
    action: 'check-in',
    clientName: 'John Smith',
    timestamp: '2024-01-10T14:30:00Z',
    loginTime: '2024-01-10T08:00:00Z'
  }, {
    id: '2',
    staffName: 'Mike Davis',
    action: 'service',
    clientName: 'Michael Brown',
    serviceType: 'Meal',
    timestamp: '2024-01-10T12:15:00Z',
    loginTime: '2024-01-10T07:30:00Z'
  }, {
    id: '3',
    staffName: 'Emily Rodriguez',
    action: 'check-out',
    clientName: 'Robert Wilson',
    timestamp: '2024-01-10T16:45:00Z',
    loginTime: '2024-01-10T09:15:00Z'
  }, {
    id: '4',
    staffName: 'Sarah Johnson',
    action: 'service',
    clientName: 'David Garcia',
    serviceType: 'Laundry',
    timestamp: '2024-01-10T15:20:00Z',
    loginTime: '2024-01-10T08:00:00Z'
  }, {
    id: '5',
    staffName: 'Mike Davis',
    action: 'check-in',
    clientName: 'Carlos Martinez',
    timestamp: '2024-01-10T13:10:00Z',
    loginTime: '2024-01-10T07:30:00Z'
  }]);
  const [smsMessage, setSmsMessage] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>(['all-staff']);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [alertType, setAlertType] = useState('general');
  
  const [isSendingSMS, setIsSendingSMS] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [noteSearch, setNoteSearch] = useState('');

  const [isImportantNote, setIsImportantNote] = useState(false);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>('');

  // Available rooms and alert handler
  const availableRooms = ['101A', '101B', '102A', '102B', '103A', '103B', '201A', '201B', '202A', '202B', '203A', '203B', '204A', '204B', '301A', '301B', '302A', '302B'];

  const handleClearAlert = (clientId: string, alertText: string, staffName: string) => {
    // This would update the clients state in a real implementation
    toast({
      title: "Alert Cleared",
      description: `"${alertText}" has been cleared by ${staffName}`
    });
  };
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Client profile state
  const [isClientProfileOpen, setIsClientProfileOpen] = useState(false);
  const [selectedProfileClient, setSelectedProfileClient] = useState<Client | null>(null);
  const [notes, setNotes] = useState<Note[]>([{
    id: '1',
    content: 'System backup completed successfully. All client data secured.',
    author: 'System Admin',
    timestamp: '2024-01-10T15:30:00Z',
    isImportant: false,
    taggedClients: [],
    status: 'active'
  }, {
    id: '2',
    content: 'Client @Maria Garcia requires immediate case manager reassignment due to conflict. Please prioritize.',
    author: 'Sarah Johnson',
    timestamp: '2024-01-10T14:15:00Z',
    isImportant: true,
    taggedClients: ['Maria Garcia'],
    status: 'active',
    alertSent: true
  }, {
    id: '3',
    content: 'Staff training mandatory for all case managers next week. @Emily Rodriguez @Robert Martinez please confirm attendance.',
    author: 'Admin',
    timestamp: '2024-01-10T10:00:00Z',
    isImportant: true,
    taggedClients: [],
    taggedStaff: ['Emily Rodriguez', 'Robert Martinez'],
    status: 'active'
  }]);
  const [metrics, setMetrics] = useState<SystemMetrics>({
    cpu: 45,
    memory: 62,
    network: 28,
    database: 71
  });

  // Static metrics (no interval — avoids unnecessary re-renders)

  // Calculate stats based on role
  const isSuperAdmin = userRole === 'super_admin';
  const isManager = userRole === 'manager';

  const statsClients = clients;
  // Bedded = client has an assigned room and isn't archived/waitlist/not_present (matches Facility Occupancy)
  const beddedClients = statsClients.filter((c: any) => c.room_id && c.status !== 'not_present' && c.status !== 'waitlist' && !c.archived_at);
  const checkedInCount = beddedClients.length;
  // Calculate total capacity from actual rooms (source of truth)
  const totalCapacity = (rooms || []).reduce((sum: number, r: any) => sum + (r.capacity || 1), 0);
  const occupancyRate = totalCapacity > 0 ? checkedInCount / totalCapacity * 100 : 0;

  // Daily Roster = bedded clients (single source of truth)
  const dailyRosterCount = checkedInCount;
  const getMetricColor = (value: number) => {
    if (value >= 80) return 'text-destructive';
    if (value >= 60) return 'text-warning';
    return 'text-success';
  };
  const getMetricBgColor = (value: number) => {
    if (value >= 80) return 'bg-destructive/20';
    if (value >= 60) return 'bg-warning/20';
    return 'bg-success/20';
  };
  const getAlertColor = (type: Alert['type']) => {
    switch (type) {
      case 'critical':
        return 'bg-destructive text-destructive-foreground';
      case 'warning':
        return 'bg-warning text-warning-foreground';
      case 'info':
        return 'bg-primary text-primary-foreground';
    }
  };
  const handleSendSMS = async () => {
    if (!smsMessage.trim()) {
      alert('Please enter a message');
      return;
    }
    setIsSendingSMS(true);
    try {
      // Get phone numbers based on selected recipients
      const phoneNumbers = getPhoneNumbersForRecipients(selectedRecipients);
      const response = await fetch('/api/send-sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipients: phoneNumbers,
          message: smsMessage,
          alertType,
          
        })
      });
      const result = await response.json();
      if (result.success) {
        alert(`SMS sent successfully to ${result.summary.successful} recipients`);
        setSmsMessage('');
      } else {
        alert(`Failed to send SMS: ${result.error}`);
      }
    } catch (error) {
      alert(`Error sending SMS: ${error.message}`);
    } finally {
      setIsSendingSMS(false);
    }
  };
  const getPhoneNumbersForRecipients = (recipients: string[]): string[] => {
    // Mock phone numbers - in real implementation, this would come from your database
    const staffPhones: Record<string, string[]> = {
      'all-staff': ['+1555123456', '+1555234567', '+1555345678', '+1555456789', '+1555567890', '+1555678901'],
      'all-managers': ['+1555123456', '+1555456789'],
      'all-supervisors': ['+1555345678', '+1555678901']
    };
    const numbers = new Set<string>();
    recipients.forEach((r) => {
      (staffPhones[r] || []).forEach((n) => numbers.add(n));
    });
    return Array.from(numbers);
  };
  const handleAddNote = () => {
    if (!newNote.trim()) return;
    const note = {
      id: `note-${Date.now()}`,
      content: newNote,
      author: user?.name || 'Admin',
      timestamp: new Date().toISOString(),
      isImportant: isImportantNote,
      taggedClients: extractTaggedClients(newNote),
      taggedStaff: extractTaggedStaff(newNote),
      status: 'active' as const
    };
    setNotes((prev) => [note, ...prev]);
    setNewNote('');
    setIsImportantNote(false);
    if (isImportantNote) {
      // Auto-send alerts for admin important notes
      alert(`Important note added and alerts sent to relevant staff`);
    }
  };
  const extractTaggedClients = (content: string) => {
    const mentions = content.match(/@([A-Z][a-z]+ [A-Z][a-z]+)/g);
    return mentions ? mentions.map((mention) => mention.substring(1)) : [];
  };
  const extractTaggedStaff = (content: string) => {
    // Similar to clients but could be extended for staff-specific tagging
    const mentions = content.match(/@([A-Z][a-z]+ [A-Z][a-z]+)/g);
    return mentions ? mentions.map((mention) => mention.substring(1)) : [];
  };
  // HTML escape function to prevent XSS
  const escapeHtml = (str: string): string => {
    const htmlEscapes: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    };
    return str.replace(/[&<>"']/g, (char) => htmlEscapes[char] || char);
  };

  const formatNoteContent = (content: string, onClientClick?: (clientName: string) => void) => {
    // First escape the entire content to prevent XSS
    const escapedContent = escapeHtml(content);

    // Then apply formatting for mentions (using escaped content)
    return escapedContent.replace(/@([A-Z][a-z]+ [A-Z][a-z]+)/g, (match, name) => {
      const clientExists = clients.some((c) => c.name === name);
      const safeName = name.replace(/"/g, '&quot;');
      if (clientExists && onClientClick) {
        return `<span class="bg-primary/20 text-primary px-1.5 py-0.5 rounded cursor-pointer hover:bg-primary/40 hover:shadow-sm transition-all font-medium" data-client-name="${safeName}">@${name}</span>`;
      }
      return `<span class="bg-primary/20 text-primary px-1 rounded">@${name}</span>`;
    });
  };

  const [selectedClientForNotes, setSelectedClientForNotes] = useState<Client | null>(null);
  const [isClientProfileFromNotesOpen, setIsClientProfileFromNotesOpen] = useState(false);
  const [filteredNotesForClient, setFilteredNotesForClient] = useState<Note[]>([]);
  const [showRoomsList, setShowRoomsList] = useState(false);

  const handleClientTagClick = (clientName: string) => {
    const client = clients.find((c) => c.name === clientName);
    if (client) {
      setSelectedClientForNotes(client);
      setIsClientProfileFromNotesOpen(true);
    }
  };
  const handleApproveNote = (noteId: string, sendAlert: boolean) => {
    setNotes((prev) => prev.map((note) => note.id === noteId ? {
      ...note,
      status: 'active',
      alertSent: sendAlert
    } : note));
    if (sendAlert) {
      alert('Note approved and alerts sent to relevant personnel');
    } else {
      alert('Note approved without sending alerts');
    }
  };
  const handleRejectNote = (noteId: string) => {
    setNotes((prev) => prev.filter((note) => note.id !== noteId));
    alert('Note rejected and removed');
  };

  // Sorting functions
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  const getSortedClients = () => {
    if (!sortColumn) return clients;
    return [...clients].sort((a, b) => {
      let valueA: any, valueB: any;
      switch (sortColumn) {
        case 'name':
          valueA = a.name.toLowerCase();
          valueB = b.name.toLowerCase();
          break;
        case 'room':
          valueA = a.room.toLowerCase();
          valueB = b.room.toLowerCase();
          break;
        case 'checkin':
          valueA = a.status === 'checked-in' ? a.checkInTime || '0' : '';
          valueB = b.status === 'checked-in' ? b.checkInTime || '0' : '';
          break;
        case 'checkout':
          valueA = a.status === 'checked-out' ? a.checkOutTime || '0' : '';
          valueB = b.status === 'checked-out' ? b.checkOutTime || '0' : '';
          break;
        case 'status':
          valueA = a.status;
          valueB = b.status;
          break;
        case 'caseworker':
          valueA = a.caseWorker.toLowerCase();
          valueB = b.caseWorker.toLowerCase();
          break;
        default:
          return 0;
      }
      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };
  const SortIcon = ({
    column


  }: {column: string;}) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? <ChevronUp className="h-4 w-4 inline ml-1" /> : <ChevronDown className="h-4 w-4 inline ml-1" />;
  };
  const handleSelectClient = (client: any) => {
    // Normalize DB record to Client shape for profile view
    const normalized: Client = {
      ...client,
      name: client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim(),
      age: client.age || (client.date_of_birth ? Math.floor((Date.now() - new Date(client.date_of_birth).getTime()) / 31557600000) : 0),
      room: client.room || client.rooms?.room_number || '',
      caseWorker: client.caseWorker || '',
      photo: client.photo || client.photo_url || ''
    };
    setSelectedProfileClient(normalized);
    setIsClientProfileOpen(true);
  };
  const handleUpdateClient = (updatedClient: Client) => {
    updateClient(updatedClient);
    setSelectedProfileClient(updatedClient);
  };
  const handleAddServiceRecord = (clientId: string, service: any) => {
    console.log('Service added for client:', clientId, service);
  };

  // Photo resolution shared across all surfaces
  const { getClientPhoto } = useClientPhotos(clients);
  // Super Admin sees: Facilities (with Site Management), User Management, Analytics, Feature Toggles
  // Manager sees: Master Census, Facilities, Staff
  // Regular Admin sees: Home, Alerts, Roster, Staff, Notes, Analytics, Services
  const sidebarItems = useMemo(() => {
    return [
      { id: 'home', label: 'Home', icon: Home },
      ...(featureTogglesLoading ? [] : isFeatureEnabled('manager_clients_tab', userRole) ? [{ id: 'clients', label: 'Clients', icon: Users }] : []),
      { id: 'alerts', label: 'Alerts', icon: Bell },
      ...(featureTogglesLoading ? [] : isFeatureEnabled('manager_staff_tab', userRole) ? [{ id: 'staff', label: 'Staff', icon: Users }] : []),
      ...(featureTogglesLoading ? [] : isFeatureEnabled('manager_facilities_tab', userRole) ? [{ id: 'facilities', label: 'Facilities', icon: Building2 }] : []),
      ...(featureTogglesLoading ? [] : isFeatureEnabled('manager_services_tab', userRole) ? [{ id: 'services', label: 'Services', icon: ClipboardCheck }] : []),
      // Rosters sidebar item retired — folded into the Clients tab (⋯ → Manage Rosters)
      ...(featureTogglesLoading ? [] : isFeatureEnabled('manager_inspections_tab', userRole) && showModuleEntry('inspections') ? [{ id: 'inspections', label: 'Inspections', icon: ClipboardCheck }] : []),
      ...(featureTogglesLoading ? [] : isFeatureEnabled('manager_notes_tab', userRole) ? [{ id: 'notes', label: 'Notes', icon: StickyNote }] : []),
      ...(featureTogglesLoading ? [] : isFeatureEnabled('manager_ai_assistant_tab', userRole) && showModuleEntry('ai_assistant') ? [{ id: 'ai-assistant', label: 'AI Assistant', icon: MessageSquare }] : []),
      ...(featureTogglesLoading ? [] : isFeatureEnabled('manager_shift_report_tab', userRole) ? [{ id: 'shift-report', label: 'Shift Report', icon: Clock }] : []),
      ...(featureTogglesLoading ? [] : isFeatureEnabled('manager_activity_logs_tab', userRole) ? [{ id: 'activity-logs', label: 'Activity Logs', icon: History }] : []),
      ...(featureTogglesLoading ? [] : isFeatureEnabled('manager_analytics_tab', userRole) && showModuleEntry('advanced_analytics') ? [{ id: 'analytics', label: 'Analytics', icon: BarChart3 }] : []),
      ...(featureTogglesLoading ? [] : isFeatureEnabled('compliance_module', userRole) ? [{ id: 'compliance', label: 'Compliance', icon: ShieldCheck }] : []),
      ...(userRole === 'super_admin' ? [{ id: 'billing', label: 'Billing', icon: CreditCard }] : []),
      ...(userRole === 'super_admin' || userRole === 'program_director' ? [{ id: 'feature-toggles', label: 'Feature Toggles', icon: ToggleLeft }] : []),
    ];
  }, [featureTogglesLoading, userRole, isFeatureEnabled, hasModule, isSuperAdminForGate]);

  // No extra admin items needed - all in main sidebar
  const adminItems: {id: string;label: string;icon: any;}[] = [];

  // Sections reachable from the header (gear, bell, profile menu) rather than the sidebar.
  // Must not be reverted by the sidebar-availability guard below.
  const headerOnlySections = new Set(['settings', 'notifications', 'help']);

  useEffect(() => {
    if (featureTogglesLoading || !activeSection) return;
    if (headerOnlySections.has(activeSection)) return;
    const availableSections = new Set(sidebarItems.map((item) => item.id));

    if (!availableSections.has(activeSection)) {
      setActiveSection('home');
      setSelectedSiteId(null);
      setSelectedSiteName(null);
    }
  }, [activeSection, featureTogglesLoading, sidebarItems]);

  // Show onboarding if needed (only after sites have loaded)
  if (!sitesLoading && sites.length === 0 && (userRole === 'manager' || userRole === 'super_admin' || userRole === 'program_director')) {
    if (!showOnboarding) {
      return <EmptyState onStartSetup={() => setShowOnboarding(true)} />;
    }
  }

  if (showOnboarding) {
    return (
      <OnboardingWizard
        onComplete={() => {
          setShowOnboarding(false);
          window.location.reload();
        }}
        onSkip={() => setShowOnboarding(false)} />);


  }

  return <SidebarProvider>
    <div className="h-screen w-full flex flex-col bg-background">
      <div className="flex-1 flex min-h-0">
      {/* Sidebar */}
      <Sidebar collapsible="icon" className="border-r">
        <SidebarContent>
          <div className="p-4 border-b">
            <div className="flex items-center gap-3">
              <img src={cimfoniLogo} alt="Cimfoni" className="h-8 w-8 object-contain shrink-0" />
              <div className="group-data-[collapsible=icon]:hidden">
                <h1 className="text-xl font-bold truncate">
                  {isSuperAdmin ? 'Super Admin' : isManager ? 'Manager' : 'Admin'}
                </h1>
                <p className="text-sm text-muted-foreground">Shelter Management</p>
              </div>
            </div>
          </div>
          
          <SidebarGroup>
            <SidebarGroupLabel className="group-data-[collapsible=icon]:hidden">Administration</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {sidebarItems.map((item) => <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      onClick={() => {
                        setActiveSection(item.id as any);
                        setSelectedSiteId(null);
                        setSelectedSiteName(null);
                      }}
                      isActive={activeSection === item.id}
                      tooltip={item.label}
                      className="w-full justify-start hover:bg-transparent data-[active=true]:bg-transparent data-[active=true]:text-primary data-[active=true]:font-medium">

                      <item.icon className="h-4 w-4 shrink-0" />
                      <span className="group-data-[collapsible=icon]:hidden ml-3">{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>)}
                {(isSuperAdmin || isManager) && adminItems.map((item) => <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      onClick={() => {
                        setActiveSection(item.id as any);
                        setSelectedSiteId(null);
                        setSelectedSiteName(null);
                      }}
                      isActive={activeSection === item.id}
                      tooltip={item.label}
                      className="w-full justify-start hover:bg-transparent data-[active=true]:bg-transparent data-[active=true]:text-primary data-[active=true]:font-medium">

                      <item.icon className="h-4 w-4 shrink-0" />
                      <span className="group-data-[collapsible=icon]:hidden ml-3">{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>)}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter className="border-t p-2">
          <SidebarTrigger className="w-full justify-center" />
        </SidebarFooter>
      </Sidebar>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="border-b sticky top-0 z-10 bg-background">
          <div className="flex h-14 items-center px-4 lg:px-6">
            <SidebarTrigger className="lg:hidden" />
            <GlobalSearchCommand />
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              
              
              {/* Alerts Button */}
              <Button
                  variant="ghost"
                  className="flex items-center gap-2 px-2"
                  onClick={() => setActiveSection('alerts')}>

                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center relative">
                  <Bell className="h-4 w-4 text-primary" />
                  {allAlerts.length > 0 &&
                    <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-destructive-foreground text-[10px] flex items-center justify-center font-bold">
                      {allAlerts.length}
                    </span>
                    }
                </div>
                
              </Button>

              {/* Settings Button */}
              <Button
                  variant="ghost"
                  className="flex items-center gap-2 px-2"
                  onClick={() => setActiveSection('settings')}>

                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Settings className="h-4 w-4 text-primary" />
                </div>
                
              </Button>
              
              {/* User Avatar & Profile Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-2">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <span className="hidden md:inline-block text-sm font-medium max-w-[120px] truncate">
                      {user?.name?.split(' ')[0] || 'Admin'}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-popover border shadow-md">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.name?.split(' ')[0] || 'Admin'}</p>
                      <p className="text-xs text-muted-foreground capitalize">{userRole || 'Super Admin'}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowMyProfile(true)} className="text-xs font-sans">
                    <User className="mr-2 h-4 w-4" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveSection('notifications')}>
                    <Bell className="mr-2 h-4 w-4" />
                    Notification Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveSection('help')}>
                    <HelpCircle className="mr-2 h-4 w-4" />
                    User Manual
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {/* Clients - Unified Client Register */}
          {activeSection === 'clients' &&
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">Clients</h1>
                <Button onClick={() => setIsNewClientOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Client
                </Button>
              </div>
              <ClientsTab onSelectClient={handleSelectClient} />
            </div>
            }

          {/* Alerts Panel */}
          {activeSection === 'alerts' && <div>
            <EnhancedAlertsSection />
          </div>}

        {activeSection === 'billing' && <BillingPage />}

        {activeSection === 'settings' &&
            <div className="space-y-6">
            <Tabs defaultValue="branding" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="branding">Branding</TabsTrigger>
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="security">Security</TabsTrigger>
                <TabsTrigger value="tools">Tools</TabsTrigger>
              </TabsList>

              <TabsContent value="branding" className="space-y-6 mt-6">
                <BrandingSettings />
              </TabsContent>

              <TabsContent value="general" className="space-y-6 mt-6">
                <SystemSettings settingsFilter="general" />
              </TabsContent>

              <TabsContent value="security" className="space-y-6 mt-6">
                <SystemSettings settingsFilter="security" />
              </TabsContent>

              <TabsContent value="tools" className="space-y-6 mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Admin Tools</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      System administration tools
                    </p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">No tools available at this time.</p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
            }

        {activeSection === 'staff' && <div className="space-y-6">
            <StaffManagementSection />
          </div>}

        {/* Rosters section retired — folded into Clients tab (⋯ → Manage Rosters) */}
        {activeSection === 'rosters' && <div className="space-y-4">
          <ClientsTab onSelectClient={handleSelectClient} />
        </div>}

        {activeSection === 'inspections' && <div className="space-y-6">
            <ModuleGate moduleKey="inspections">
              <InspectionsModule />
            </ModuleGate>
          </div>}

        {activeSection === 'notes' && <div className="space-y-6">
            {/* Administrative Notes */}
            <Collapsible defaultOpen={true}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="hover:bg-accent/50 transition-colors cursor-pointer">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5" />
                        Administrative Notes
                      </CardTitle>
                      <ChevronDown className="h-4 w-4" />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-6">
                    {/* Add Note Section */}
                    <div className="space-y-4 border-b border-border pb-6">
                      
                      <div className="space-y-2">
                        
                        <textarea className="w-full p-3 border rounded-md min-h-[120px] resize-none" placeholder="Enter administrative note... Use @ClientName or @StaffName to tag people in the note." value={newNote} onChange={(e) => setNewNote(e.target.value)} />
                        <p className="text-xs text-muted-foreground">
                          Tip: Use @Name to tag clients or staff (e.g., @John Smith)
                        </p>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <input type="checkbox" id="important-admin" checked={isImportantNote} onChange={(e) => setIsImportantNote(e.target.checked)} className="rounded" />
                          <label htmlFor="important-admin" className="text-sm font-medium">
                            Mark as Important (sends automatic alerts to tagged personnel)
                          </label>
                        </div>

                        <Button onClick={handleAddNote} disabled={!newNote.trim()} className="bg-primary hover:bg-primary/90">
                          <Send className="h-4 w-4 mr-2" />
                          Add Note
                        </Button>
                      </div>
                    </div>

                    {/* Notes Log Section */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <h3 className="font-medium flex items-center gap-2">
                          <BookOpen className="h-4 w-4" />
                          Recent Notes
                        </h3>
                        <Input
                          value={noteSearch}
                          onChange={(e) => setNoteSearch(e.target.value)}
                          placeholder="Search notes, authors, tagged names..."
                          className="w-full sm:w-72"
                        />
                      </div>
                      <div className="space-y-4">
                        {notes
                          .filter((note) => note.status === 'active')
                          .filter((note) => {
                            const q = noteSearch.trim().toLowerCase();
                            if (!q) return true;
                            return [note.content, note.author, ...(note.taggedClients || []), ...(note.taggedStaff || [])]
                              .filter(Boolean)
                              .join(' ')
                              .toLowerCase()
                              .includes(q);
                          })
                          .map((note) => <Card key={note.id} className={`${note.isImportant ? 'border-l-4 border-l-primary' : ''}`}>

                            <CardContent className="p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">{note.author}</span>
                                  {note.isImportant && <Badge variant="default" className="text-xs">
                                      <Star className="h-3 w-3 mr-1" />
                                      Important
                                    </Badge>}
                                  {note.alertSent && <Badge variant="outline" className="text-xs text-success">
                                      Alert Sent
                                    </Badge>}
                                </div>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(note.timestamp).toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>

                              <div
                                className="text-sm mb-2"
                                dangerouslySetInnerHTML={{
                                  __html: formatNoteContent(note.content, handleClientTagClick)
                                }}
                                onClick={(e) => {
                                  const target = e.target as HTMLElement;
                                  const clientName = target.getAttribute('data-client-name');
                                  if (clientName) {
                                    handleClientTagClick(clientName);
                                  }
                                }} />


                              {(note.taggedClients?.length > 0 || note.taggedStaff?.length > 0) && <div className="flex items-center gap-2 mt-2">
                                  <Tag className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground">
                                    Tagged: {[...(note.taggedClients || []), ...(note.taggedStaff || [])].join(', ')}
                                  </span>
                                </div>}
                            </CardContent>
                          </Card>)}
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Pending Approvals (if any from staff) */}
            {notes.filter((note) => note.status === 'pending_approval').length > 0 && <Collapsible defaultOpen={true}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="hover:bg-accent/50 transition-colors cursor-pointer">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Bell className="h-5 w-5 text-warning" />
                        Pending Approvals
                      </CardTitle>
                      <ChevronDown className="h-4 w-4" />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent>
                  <div className="space-y-4">
                    {notes.filter((note) => note.status === 'pending_approval').map((note) => <Card key={note.id} className="border-l-4 border-l-warning bg-warning/5">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{note.author}</span>
                              <Badge variant="secondary" className="text-xs">
                                Awaiting Approval
                              </Badge>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {new Date(note.timestamp).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                            </span>
                          </div>

                          <div
                              className="text-sm mb-3"
                              dangerouslySetInnerHTML={{
                                __html: formatNoteContent(note.content, handleClientTagClick)
                              }}
                              onClick={(e) => {
                                const target = e.target as HTMLElement;
                                const clientName = target.getAttribute('data-client-name');
                                if (clientName) {
                                  handleClientTagClick(clientName);
                                }
                              }} />


                          {(note.taggedClients?.length > 0 || note.taggedStaff?.length > 0) && <div className="flex items-center gap-2 mb-3">
                              <Tag className="h-3 w-3 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">
                                Tagged: {[...(note.taggedClients || []), ...(note.taggedStaff || [])].join(', ')}
                              </span>
                            </div>}

                          <div className="flex gap-2 pt-3 border-t">
                            <Button size="sm" className="bg-success hover:bg-success/90" onClick={() => handleApproveNote(note.id, true)}>
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Approve & Send Alert
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleApproveNote(note.id, false)}>
                              Approve Without Alert
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleRejectNote(note.id)}>
                              Reject
                            </Button>
                          </div>
                        </CardContent>
                      </Card>)}
                  </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>}
          </div>}

        {/* Alert Management Center */}
        {activeSection === 'alerts' && <Card>
          <CardHeader className="py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Send Text Alert
              </CardTitle>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground">SMS</span>
                  <Switch checked={smsEnabled} onCheckedChange={setSmsEnabled} className="scale-75" />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground">Email</span>
                  <Switch checked={emailEnabled} onCheckedChange={setEmailEnabled} className="scale-75" />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-sm font-medium">Recipients</label>
                <div className="border rounded-md p-2 max-h-40 overflow-y-auto space-y-1">
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">Groups</p>
                  {[
                      { value: 'all-staff', label: 'All Staff' },
                      { value: 'all-managers', label: 'All Managers' },
                      { value: 'all-supervisors', label: 'All Supervisors' }].
                      map((group) =>
                      <label key={group.value} className="flex items-center gap-2 py-0.5 px-1 rounded hover:bg-muted/50 cursor-pointer text-sm">
                      <input
                          type="checkbox"
                          className="rounded"
                          checked={selectedRecipients.includes(group.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRecipients((prev) => [...prev, group.value]);
                            } else {
                              setSelectedRecipients((prev) => prev.filter((r) => r !== group.value));
                            }
                          }} />
                        
                      {group.label}
                    </label>
                      )}
                  {staffMembers.length > 0 &&
                      <>
                      <Separator className="my-1" />
                      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">Individual Staff</p>
                      {staffMembers.map((s: any) =>
                        <label key={s.id} className="flex items-center gap-2 py-0.5 px-1 rounded hover:bg-muted/50 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            className="rounded"
                            checked={selectedRecipients.includes(s.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedRecipients((prev) => [...prev, s.id]);
                              } else {
                                setSelectedRecipients((prev) => prev.filter((r) => r !== s.id));
                              }
                            }} />
                          
                          {s.full_name || s.email}
                        </label>
                        )}
                    </>
                      }
                </div>
                {selectedRecipients.length > 0 &&
                    <p className="text-xs text-muted-foreground">{selectedRecipients.length} selected</p>
                    }
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Alert Type</label>
                <select className="w-full p-2 border rounded-md text-sm" value={alertType} onChange={(e) => setAlertType(e.target.value)}>
                  <option value="general">General Reminder</option>
                  <option value="emergency">Emergency Alert</option>
                  <option value="shift">Shift Change</option>
                  <option value="training">Training Reminder</option>
                  <option value="maintenance">System Maintenance</option>
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Message</label>
              <textarea className="w-full p-2 border rounded-md h-16 resize-none text-sm" placeholder="Type your alert message here..." maxLength={160} value={smsMessage} onChange={(e) => setSmsMessage(e.target.value)} />
              <p className="text-xs text-muted-foreground">{smsMessage.length}/160 characters</p>
            </div>

            <div className="flex justify-end">
              <Button size="sm" onClick={handleSendSMS} disabled={isSendingSMS || !smsMessage.trim() || !smsEnabled && !emailEnabled}>
                <Phone className="h-3.5 w-3.5 mr-1.5" />
                {isSendingSMS ? 'Sending...' : 'Send Alert'}
              </Button>
            </div>
          </CardContent>
        </Card>}

        {/* Analytics Dashboard */}
        {activeSection === 'analytics' &&
            <ModuleGate moduleKey="advanced_analytics">
              <AnalyticsHub
                clients={clients}
                alerts={alerts}
                totalCapacity={totalCapacity}
                checkedInCount={checkedInCount}
                dailyRosterCount={dailyRosterCount}
                userName={user?.name || 'Unknown'} />
            </ModuleGate>
            }

        {/* Compliance Module */}
        {activeSection === 'compliance' && <ComplianceModule />}

        {/* Super Admin Home */}
        {activeSection === 'home' && isSuperAdmin &&
            <SuperAdminHome
              clients={clients}
              rooms={rooms}
              sites={sites}
              alerts={alerts}
              totalCapacity={totalCapacity}
              checkedInCount={checkedInCount}
              dailyRosterCount={dailyRosterCount}
              userName={user?.name || 'Unknown'}
              onSelectFacility={(siteId) => { setActiveSection('facilities'); setSelectedSiteId(siteId); }}
              showPerformanceCard={true}
            />
            }

        {/* Facilities Management - Super Admin sees Site Management only (no census) */}
        {activeSection === 'facilities' && isSuperAdmin &&
            <SiteManagement isDemo={false} />
            }

        {/* Facilities Management - Other admins get FacilitySelector + Census drill-down */}
        {activeSection === 'facilities' && !isSuperAdmin && !selectedSiteId &&
            <FacilitySelector
              onSiteSelect={(siteId, siteName) => {
                setSelectedSiteId(siteId);
                setSelectedSiteName(siteName);
              }} />

            }

        {/* Site Daily Census - Non-super-admin only */}
        {activeSection === 'facilities' && !isSuperAdmin && selectedSiteId && selectedSiteName &&
            <SiteDailyCensus
              siteId={selectedSiteId}
              siteName={selectedSiteName}
              onBack={() => {
                setSelectedSiteId(null);
                setSelectedSiteName(null);
              }} />

            }

        {/* User Management - accessible via staff tab */}

        {/* Feature Toggle Management */}
        {activeSection === 'feature-toggles' && <FeatureToggleManagement />}

        {/* Activity Logs */}
        {activeSection === 'activity-logs' && <UserActivityLogs isDemo={false} />}

        {/* Shift Report */}
        {activeSection === 'shift-report' && <ShiftReport onBack={() => setActiveSection('home')} />}

        {/* Roster Management - now in Clients tab */}

        {/* System Settings - rendered earlier in file at line 1358 */}

         {/* Help & Support */}
         {activeSection === 'help' && <div className="space-y-4">
            <UserGuide />
          </div>}

         {/* AI Chat & FAQ */}
         {activeSection === 'ai-chat' && <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* AI Help Chat */}
              <ModuleGate moduleKey="ai_assistant"><AIHelpChat /></ModuleGate>

              {/* FAQ Section */}
              <Card className="h-[600px] flex flex-col">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="h-5 w-5" />
                    Frequently Asked Questions
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto">
                  <div className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm font-medium">How do I check in a client?</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        Navigate to the Daily Census section, click "Check In Client", search for the client by name or ID, and assign them to an available bed.
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm font-medium">What is a returnee?</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        A returnee is a client who was previously unneeded and has returned to receive a bed. You can add observations before checking them into a room in their client profile.
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm font-medium">How do I add services for a client?</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        Open the client's profile, go to the Services tab, and click "Add Service" to record meals, transit cards, laundry cards, or beverages.
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm font-medium">How do I send alerts to staff?</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        Go to Alert Management, create a new alert, select recipients, choose the priority level, and send via email or SMS.
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm font-medium">How do I view analytics?</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        Access the Analytics Dashboard from the sidebar to view occupancy rates, system health, service analytics, and staff performance metrics.
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm font-medium">How do I manage the roster?</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        Click on Roster Management to create bed grids, assign beds to clients, and view the current roster layout.
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm font-medium">What should I do if a client is unneeded?</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        Mark the client as "unneeded" in their profile. This will flag them for follow-up and track their status. If they return, mark them as a returnee.
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>}

        {/* Services Analytics */}
        {activeSection === 'services' &&
            <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">Services Analytics</h1>
                <p className="text-muted-foreground mt-1">
                  Detailed breakdown of services provided to clients
                </p>
              </div>
            </div>
            <ServicesAnalytics clients={clients} currentUserName={user?.name || 'User'} />
          </div>
            }

        {/* AI Assistant */}
        {activeSection === 'ai-assistant' &&
            <div className="space-y-6 h-[calc(100vh-12rem)]">
            <div>
              <h1 className="text-2xl font-bold">AI Dashboard Assistant</h1>
              <p className="text-muted-foreground mt-1">
                Ask questions about your facilities and operations
              </p>
            </div>
            <div className="h-[calc(100%-5rem)]">
              <ModuleGate moduleKey="ai_assistant"><AIHelpChat /></ModuleGate>
            </div>
          </div>
            }

        {/* Alert Notification Settings */}
        {activeSection === 'notifications' &&
            <AlertNotificationSettings />
            }

        {/* Attendance Log - now in Clients tab */}

        </main>
      </div>
      </div>

      {/* Footer */}
      <footer className="text-center py-3 border-t bg-background shrink-0">
        <p className="text-xs text-muted-foreground/60">
          Powered by Mitch Solutions
        </p>
      </footer>

      {/* Client Profile Modal */}
      {selectedProfileClient && <ClientProfile client={selectedProfileClient} isOpen={isClientProfileOpen} onClose={() => setIsClientProfileOpen(false)} getClientPhoto={getClientPhoto} onUpdateClient={handleUpdateClient} onAddServiceRecord={handleAddServiceRecord} />}

      {/* New Client Form */}
      <NewClientForm
        isOpen={isNewClientOpen}
        onClose={() => setIsNewClientOpen(false)}
        siteId={sites.length > 0 ? sites[0].id : undefined}
        onSubmit={(clientData) => {
          createClient({
            ...clientData,
            siteId: sites.length > 0 ? sites[0].id : undefined
          } as any);
          setIsNewClientOpen(false);
        }} />

      
      {/* Service Alerts Modal */}
      <Dialog open={showServiceAlertsModal} onOpenChange={setShowServiceAlertsModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Service Alerts - Monthly Summary
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground mb-4">
              Recent service alerts sorted by most recent activity
            </div>
            <div className="space-y-3">
              {allAlerts.
              filter((alert) => alert.category === 'service').
              map((alert) =>
              <div key={alert.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Badge className="text-xs border bg-green-100 text-green-800 border-green-200">
                        SERVICE
                      </Badge>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{alert.title}</p>
                        <p className="text-xs text-muted-foreground">{alert.description}</p>
                        <div className="flex items-center gap-4 mt-1">
                          {alert.createdBy &&
                      <span className="text-xs text-muted-foreground">
                              Created by: <span className="font-medium">{alert.createdBy}</span>
                            </span>
                      }
                          {alert.clientId &&
                      <Badge variant="outline" className="text-xs">
                              Client Alert
                            </Badge>
                      }
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {new Date(alert.timestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(alert.timestamp).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
              )}
              {allAlerts.filter((alert) => alert.category === 'service').length === 0 &&
              <div className="text-center py-8 text-muted-foreground">
                  No service alerts found
                </div>
              }
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Profile Dialog from Notes */}
      {selectedClientForNotes &&
      <ClientProfile
        client={selectedClientForNotes}
        isOpen={isClientProfileFromNotesOpen}
        onClose={() => setIsClientProfileFromNotesOpen(false)}
        getClientPhoto={getClientPhoto}
        onUpdateClient={handleUpdateClient}
        onAddServiceRecord={handleAddServiceRecord}
        initialTab="notes"
        availableRooms={availableRooms}
        onClearAlert={handleClearAlert} />

      }
      <MyProfileDialog open={showMyProfile} onOpenChange={setShowMyProfile} />
      <AlertDetailDialog alert={selectedAlert} open={isAlertDialogOpen} onOpenChange={setIsAlertDialogOpen} onAlertResolved={(title) => {
        if (selectedAlert?.clientId) {
          const client = clients.find(c => c.id === selectedAlert.clientId);
          if (client && client.alerts) {
            const updatedAlerts = client.alerts.filter(a => a !== title);
            updateClient({ ...client, alerts: updatedAlerts });
          }
        }
      }} />
    </div>
  </SidebarProvider>;
};

export default AdminDashboard;